import { clientConfig } from "../../config";
import { PEN_HERO_BRANDS } from "./constants";
import { CarePlusMedication, CarePlusDosageOption } from "../ChooseTreatmentCarePlus/types";

export const buildImageUrl = (image?: { base_path: string; name: string } | null): string | null =>
  image ? `${clientConfig.IMAGES_URL}${image.base_path}/${encodeURIComponent(image.name)}` : null;

export const minDosagePrice = (med: CarePlusMedication): number => {
  const prices = med.dosages.map((d) => d.price).filter((p) => p > 0);
  return prices.length ? Math.min(...prices) : 0;
};

export const dosageRange = (med: CarePlusMedication): string => {
  if (!med.dosages.length) return "";
  const sorted = [...med.dosages].sort((a, b) => parseFloat(a.dosage_str) - parseFloat(b.dosage_str));
  const min = sorted[0]?.dosage_str || "";
  const max = sorted[sorted.length - 1]?.dosage_str || "";
  return min === max ? min : `${min} — ${max}`;
};

export const packLabel = (d: CarePlusDosageOption): string => {
  if (d.quantity_title) return d.quantity_title;
  if (!d.quantity) return "";
  return `${d.quantity} ${d.quantity === 1 ? "tablet" : "tablets"}`;
};

const INTAKE_FREQUENCY: { match: RegExp; key: string; fallback: string }[] = [
  { match: /saxenda|victoza|liraglutid/i, key: "freq_once_daily_injection", fallback: "Once-daily injection" },
  { match: /orlistat|xenical|alli/i, key: "freq_capsule_with_meals", fallback: "Capsule with meals" },
  { match: /wegovy.*(tablet|pill)/i, key: "freq_once_daily_tablet", fallback: "Once-daily tablet" },
  { match: /foundayo|orforglipron/i, key: "freq_once_daily_tablet", fallback: "Once-daily tablet" },
];

export const intakeFrequency = (med: CarePlusMedication, t: (key: string, fallback: string) => string): string => {
  const hit = INTAKE_FREQUENCY.find((f) => f.match.test(med.title));
  return hit ? t(hit.key, hit.fallback) : t("freq_once_weekly_injection", "Once-weekly injection");
};

export const usageCadence = (med: CarePlusMedication, t: (key: string, fallback: string) => string): string =>
  INTAKE_FREQUENCY.some((f) => f.match.test(med.title))
    ? t("used_daily", "Used daily")
    : t("used_weekly", "Used weekly");

export const contentBrand = (title: string): string => {
  const t = (title || "").toLowerCase();
  if (t.includes("mounjaro")) return "mounjaro";
  if (t.includes("wegovy") && /(tablet|pill)/i.test(t)) return "wegovy_tablet";
  if (t.includes("wegovy")) return "wegovy";
  if (t.includes("saxenda")) return "saxenda";
  if (t.includes("xenical")) return "xenical";
  if (t.includes("orlistat")) return "orlistat";
  if (t.includes("nevolat")) return "nevolat";
  return "";
};

// advantageKey: translation key in choose_treatment_care_plus ("-" where a language has no copy).
export const AVG_WEIGHT_LOSS: Record<string, { pct: string; advantageKey: string }> = {
  wegovy: { pct: "-20.7%", advantageKey: "advantage_heart_health" },
  wegovy_tablet: { pct: "-17%", advantageKey: "advantage_heart_health" },
  mounjaro: { pct: "-22.5%", advantageKey: "advantage_appetite_blood_sugar" },
};

// Shared by Choose Treatment and Choose Plan; the 100vw base keeps the pen at content width inside Choose Plan's padded column.
export const medicationHeroImageClassName = (title: string): string =>
  `max-h-[160px] object-contain object-center pointer-events-none motion-safe:animate-product-slide-up ${
    PEN_HERO_BRANDS.includes(contentBrand(title)) ? "w-[calc(100vw-32px)] max-w-full md:w-full" : "max-w-[240px]"
  }`;
